import { useState, useRef, useEffect } from "react";
import {
  FaTimes,
  FaEdit,
  FaCalendarAlt,
  FaUser,
  FaPaw,
  FaUserTie,
  FaCheck,
  FaBan,
  FaTag,
} from "react-icons/fa";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import LoaderSpiner from "../LoaderSpiner/LoaderSpiner";
import { useColor } from "../../hooks/color/useColor";
import StaffSelector from "../../pages/Orders/StaffSelector";
import OrderNumberBadge from "./OrderNumberBadge";
import { httpService } from "../../services/httpService";

// ----------- Helper functions (same as AppointmentQueueTooltip) -----------
function parseTimeRange(timeRange) {
  if (!timeRange) return null;
  try {
    const separators = ["–", "-", "to", "TO"];
    let startTime = "";
    let endTime = "";
    for (const sep of separators) {
      if (timeRange.includes(sep)) {
        const parts = timeRange.split(sep).map(p => p.trim());
        if (parts.length >= 2) {
          startTime = parts[0];
          endTime = parts[1];
          break;
        }
      }
    }
    if (!startTime && !endTime) {
      startTime = timeRange.trim();
      const startDate = parseTimeToDate(startTime);
      if (startDate) {
        const endDate = new Date(startDate.getTime() + 60 * 60 * 1000);
        endTime = endDate.toLocaleTimeString("en-IN", {
          hour: "numeric",
          minute: "2-digit",
        });
      }
    }
    return { startTime, endTime };
  } catch (error) {
    console.error("Error parsing time range:", error);
    return null;
  }
}

function parseTimeToDate(timeStr) {
  if (!timeStr) return null;
  try {
    const cleanTime = timeStr.trim().toUpperCase();
    const time = new Date(`2000-01-01 ${cleanTime}`);
    if (!isNaN(time.getTime())) return time;
    const match = cleanTime.match(/(\d+):?(\d*)\s*(AM|PM)?/i);
    if (match) {
      let hours = parseInt(match[1], 10);
      const minutes = match[2] ? parseInt(match[2], 10) : 0;
      const meridiem = (match[3] || "").toUpperCase();
      if (meridiem === "PM" && hours < 12) hours += 12;
      if (meridiem === "AM" && hours === 12) hours = 0;
      return new Date(2000, 0, 1, hours, minutes);
    }
    return null;
  } catch (error) {
    console.error("Error parsing time:", error);
    return null;
  }
}

function generateTimeSlotsFromRange(startTime, endTime, interval = 15) {
  const slots = [];
  const start = parseTimeToDate(startTime);
  const end = parseTimeToDate(endTime);
  if (!start || !end) return generateDefaultTimeSlots();
  let current = new Date(start);
  while (current <= end) {
    const formatted = current.toLocaleTimeString("en-IN", {
      hour: "numeric",
      minute: "2-digit",
    });
    slots.push(formatted);
    current = new Date(current.getTime() + interval * 60 * 1000);
  }
  return slots;
}

function generateDefaultTimeSlots() {
  const slots = [];
  let start = 8 * 60;
  const end = 20 * 60;
  while (start <= end) {
    const hours = Math.floor(start / 60);
    const minutes = start % 60;
    const formatted = new Date(0, 0, 0, hours, minutes).toLocaleTimeString(
      "en-IN",
      { hour: "numeric", minute: "2-digit" }
    );
    slots.push(formatted);
    start += 15;
  }
  return slots;
}
// -------------------------------------------------------------------------

const AppointmentConfirmTooltip = ({
  visible,
  position,
  placement,
  refId,
  closeTooltip,
  employees = [],
  onViewOrder,          // optional callback when clicking order number
  onRescheduleClick,
}) => {
  const ref = useRef(null);
  const { getColor } = useColor();

  // ----- Fetching & loading state -----
  const [loading, setLoading] = useState(true);
  const [appointment, setAppointment] = useState(null);

  // ----- Editable fields -----
  const [appointmentDate, setAppointmentDate] = useState("");
  const [appointmentTime, setAppointmentTime] = useState("");
  const [timeSlots, setTimeSlots] = useState(generateDefaultTimeSlots());
  const [selectedStaff, setSelectedStaff] = useState("");
  const [selectedStaffName, setSelectedStaffName] = useState("");
  const [isStaffModified, setIsStaffModified] = useState(false);
  const [serviceDuration, setServiceDuration] = useState("");

  // ----- Saving / cancelling states -----
  const [isSaving, setIsSaving] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
 
  const [showFullNotes, setShowFullNotes] = useState(false);
  // ----- Fetch appointment on open (similar to BookingConfirmTooltip) -----
  useEffect(() => {
    if (!visible || !refId) return;

    const fetchAppointment = async () => {
      setLoading(true);
      try {
        // ✅ Replace with your GET endpoint
        const response = await httpService.get(
          `/vendorappointment/getVendorAppointment/${refId}`
        );
        if (response.code === 200) {
          setAppointment(response.data);
        } else {
          toast.error("Failed to load appointment details");
          closeTooltip();
        }
      } catch (error) {
        console.error("Error fetching appointment:", error);
        toast.error("Failed to load appointment data");
        closeTooltip();
      } finally {
        setLoading(false);
      }
    };
    fetchAppointment();
  }, [visible, refId, closeTooltip]);

  // ----- Populate local state when appointment data arrives -----
  useEffect(() => {
    if (!appointment) return;

    // Parse date
    const dateStr = appointment.start_date || appointment.appointment_date;
    const dateOnly = dateStr
      ? dateStr.split("T")[0]
      : new Date().toISOString().split("T")[0];
    setAppointmentDate(dateOnly);

    // Parse time
    let initialTime = "";
    if (appointment.start_time) {
      initialTime = appointment.start_time?.includes("–") ? "" : appointment.start_time;
    } else if (appointment.time_range) {
      const timeRange = parseTimeRange(appointment.time_range.toLowerCase());
      if (timeRange && timeRange.startTime) {
        initialTime = timeRange.startTime;
      }
    }
    setAppointmentTime(initialTime);

    // Generate appropriate time slots
    if (appointment.order_type === "online" && appointment.time_range) {
      const timeRange = parseTimeRange(appointment.time_range.toLowerCase());
      if (timeRange && timeRange.startTime && timeRange.endTime) {
        setTimeSlots(
          generateTimeSlotsFromRange(timeRange.startTime, timeRange.endTime)
        );
      } else {
        setTimeSlots(generateDefaultTimeSlots());
      }
    } else {
      setTimeSlots(generateDefaultTimeSlots());
    }

    // Staff
    const techId =
      appointment.technician_id || appointment.employee_id || "";
    setSelectedStaff(techId);
    setSelectedStaffName(appointment.employee_name || "");
    setIsStaffModified(false);

    // Fetch service duration (like existing tooltip)
    fetchServiceDuration(appointment.service_id, appointment.type);
  }, [appointment]);

  // ----- Fetch service duration -----
  const fetchServiceDuration = async (serviceId, type) => {
    if (!serviceId) return;
    try {
      // ✅ Replace with your own endpoints if needed
      const endpoint =
        type === "grooming"
          ? `/vendorservice/getvendorservice/`
          : type === "vet_clinic"
          ? `/vendorvetclinicservice/getvendorvetclinicservice/`
          : `/vendordogwalkingservice/getvendordogwalkingservice/`;
      const response = await httpService.get(`${endpoint}${serviceId}`);
      const data = response.data;
      if (data?.duration?.flat) {
        const { hour = 0, minutes = 0 } = data.duration.flat;
        const parts = [];
        if (hour > 0) parts.push(`${hour} Hour${hour > 1 ? "s" : ""}`);
        if (minutes > 0) parts.push(`${minutes} Minute${minutes > 1 ? "s" : ""}`);
        setServiceDuration(parts.join(" "));
      }
    } catch (error) {
      console.error("Failed to fetch service duration:", error);
    }
  };

  // ----- Staff selection handler -----
  const handleStaffChange = (staffId, staffName) => {
    setSelectedStaff(staffId);
    setSelectedStaffName(staffName);
    setIsStaffModified(
      staffId !== (appointment?.technician_id || appointment?.employee_id || "")
    );
  };

  // ----- Reject appointment (endpoint can be kept similar) -----
  const handleCancelAppointment = async () => {
    const result = await Swal.fire({
      title: "Reject Appointment?",
      text: "Are you sure you want to reject this appointment?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, reject it!",
      cancelButtonText: "No, keep it",
    });

    if (!result.isConfirmed) return;

    setIsCancelling(true);
    try {
      // ✅ Replace with your reject endpoint if different
      await httpService.put(
        `/vendorappointment/updateVendorAppointmentStatus/${refId}`,
        { status: "cancelled" }
      );

      Swal.fire({
        title: "Cancelled!",
        text: "Appointment has been rejected successfully.",
        icon: "success",
        confirmButtonColor: "#10b981",
      });

      if (window.refreshAppointmentData) window.refreshAppointmentData();
      setTimeout(() => closeTooltip(), 1500);
    } catch (error) {
      console.error("Error cancelling appointment:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to reject appointment. Please try again.",
        icon: "error",
      });
    } finally {
      setIsCancelling(false);
    }
  };

  // ----- Confirm appointment (DIFFERENT ENDPOINT from AppointmentQueueTooltip) -----
  const handleConfirmAppointment = async () => {
    // Basic validation
    if (!selectedStaff) {
      Swal.fire({
        title: "Missing Information",
        text: "Please select staff member.",
        icon: "warning",
      });
      return;
    }
    
    if (!appointmentTime) {
      Swal.fire({
        title: "Missing Information",
        text: "Please select Time Slot.",
        icon: "warning",
      });
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        staffId: selectedStaff,
        time_slot: appointmentTime,
        ref_id: refId,
      };

      const response = await httpService.post(
        `/vendorappointment/updateVendorAppointmentStaff`,
        payload
      );

      if (response.code === 200 || response.status) {
        toast.success("Appointment confirmed successfully!");
        closeTooltip();
        if (window.refreshAppointmentData) window.refreshAppointmentData();
      } else {
        throw new Error(response.message || "Confirmation failed");
      }
    } catch (error) {
      console.error("Confirm error:", error);
      toast.error("Failed to confirm appointment. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  // ----- Outside click -----
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        e.target.closest(".swal2-container") ||
        e.target.closest(".swal2-popup") ||
        e.target.closest(".reschedule-drawer-wrapper")
      )
        return;
      if (ref.current && !ref.current.contains(e.target)) {
        closeTooltip();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [closeTooltip]);

  // Helper function to truncate text
  const truncateText = (text, maxLength = 70) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };


  // ----- Render -----
  if (!visible) return null;
  if (loading) {
    return (
      <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/20 backdrop-blur-sm">
        <LoaderSpiner text="Loading..." />
      </div>
    );
  }
  if (!appointment) return null;

  const serviceColor = getColor(
    "serviceCategoryColors",
    appointment.service_type_name
  );

  // For StaffSelector compatibility
  const staffSelectorAppointment = {
    ...appointment,
    technician_id: selectedStaff,
    employee_name: selectedStaffName,
    start_date: appointmentDate,
    start_time: appointmentTime,
    duration: serviceDuration,
    service_category_type_id: appointment.service_category_type_id,
    ref_id: refId,
  };

  const handleStaffSelectorChange = (index, field, value) => {
    if (field === "technician_id") {
      const selectedEmployee = employees.find(
        (emp) =>
          emp._id === value || emp.id === value || emp.employee_id === value
      );
      handleStaffChange(value, selectedEmployee?.name || "");
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40"
        onClick={closeTooltip}
      />
      <div
        ref={ref}
        className="absolute z-50 w-[520px] max-w-lg bg-white rounded-2xl border-4 animate-fade-in"
        style={{
          left: position.x,
          top: position.y,
          transform:
            placement === "center"
              ? "translate(-50%, -50%)"
              : placement === "right"
              ? "translateY(-50%)"
              : placement === "left"
              ? "translateY(-50%)"
              : placement === "bottom"
              ? "translateX(-50%) translateY(0)"
              : "translateX(-50%) translateY(-100%)",
          borderColor: serviceColor,
          boxShadow: `0 25px 50px -12px rgba(0,0,0,0.25), 0 0 0 3px ${serviceColor}20`,
        }}
      >
        <button
          onClick={closeTooltip}
          className="absolute top-2 right-2 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all duration-200 z-10"
        >
          <FaTimes className="w-4 h-4" />
        </button>

        <div className="px-6 py-4">
          {/* Line 1: Order number & service type */}
          <div className="flex items-center gap-3 mb-3">
            {appointment.order_number && (
              <OrderNumberBadge
                orderNumber={appointment.order_number}
                orderType={appointment.order_type}
                onClick={() => {
                  closeTooltip();
                  if (onViewOrder) {
                    onViewOrder({order_id: appointment.order_id},"details",);
                  }
                }}
                className="inline-block px-2 py-1 text-md font-semibold rounded w-fit text-black border border-black"
              />
            )}
            <div className="text-lg font-bold text-black">
              {appointment.service_type_name}
            </div>
          </div>

          {/* Line 2: Date & Time */}
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                <FaCalendarAlt className="inline w-3 h-3 mr-1 text-gray-500" />
                Date
              </label>
              <input
                type="date"
                value={appointmentDate}
                readOnly
                onChange={(e) => setAppointmentDate(e.target.value)}
                min={new Date().toISOString().split("T")[0]}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                <FaCalendarAlt className="inline w-3 h-3 mr-1 text-gray-500" />
                Time
              </label>
              <select
                value={appointmentTime}
                onChange={(e) => setAppointmentTime(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select time</option>
                {timeSlots.map((slot) => (
                  <option key={slot} value={slot}>
                    {slot}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Line 3: Service name & price */}
          <div className="flex items-center justify-between mb-3">
            <div className="text-base font-bold text-gray-900">
              {appointment.service_name || ""} ({serviceDuration || ""})
            </div>
            <div className="text-lg font-bold text-gray-900 flex gap-1">
              ₹
              <span>{Number(appointment.final_total).toFixed(0)}</span>
            </div>
          </div>

          <hr className="border-gray-300 my-3" />

          {/* Line 4: Pet */}
          <div className="flex items-center gap-2 mb-3">
            <FaPaw className="w-4 h-4 text-gray-500" />
            <div className="text-sm font-medium text-gray-800">
              <span className="font-semibold">{appointment.pet_name}</span>
              {appointment.pet_info?.type_info?.name && (
                <span className="text-gray-600"> ({appointment.pet_info.type_info.name})</span>
              )}
              {appointment.pet_info?.size_info?.size_label && ` • ${appointment.pet_info?.size_info?.size_label}`}
              {appointment.pet_info?.breed_info?.breed_name && ` • ${appointment.pet_info?.breed_info?.breed_name}`}
            </div>
            {appointment.pet_info?.tags && appointment.pet_info.tags.length > 0 && (
                  <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                    {appointment.pet_info.tags.map((tag, index) => (
                      <span 
                        key={index}
                        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                      >
                        <FaTag className="w-3 h-3" />
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
          </div>
           {/* Line 4.5 – Medical Conditions */}
            {/* {appointment.pet_info?.medical_condition && appointment.pet_info.medical_condition.length > 0 && (
              <div className="flex items-start gap-2 mb-3">
                <svg 
                  className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
                  />
                </svg>
                <div className="text-sm text-gray-700 flex-1">
                  <span className="font-semibold text-red-600">Medical Conditions:</span>{' '}
                  <span className="text-gray-700">
                    {appointment.pet_info.medical_condition.join(', ')}
                  </span>
                </div>
              </div>
            )} */}

          {/* Line 5: Client */}
          <div className="flex items-center gap-2 mb-3">
            <FaUser className="w-4 h-4 text-gray-500" />
            <div className="text-sm font-medium text-gray-800">
              <span className="font-semibold">
                {appointment.client ||
                  `${appointment.client_name || ""}`.trim() ||
                  ""}
              </span>
              {appointment.client_phone && ` (${appointment.client_phone})`}
            </div>
          </div>

          {/* Line 6: Notes */}
           {appointment.notes && (
            <div className="flex items-start gap-2 mb-3">
              <svg 
                className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" 
                />
              </svg>
              <div className="text-sm text-gray-700 flex-1">
                <span className="font-semibold">Notes:</span>{' '}
                {showFullNotes ? appointment.notes : truncateText(appointment.notes)}
                {appointment.notes.length > 70 && (
                  <button
                    onClick={() => setShowFullNotes(!showFullNotes)}
                    className="ml-2 text-blue-600 hover:text-blue-800 font-medium text-xs focus:outline-none"
                  >
                    {showFullNotes ? 'Show less' : 'Show more'}
                  </button>
                )}
              </div>
            </div>
          )}
          {/* Line 7: Staff */}
          <div className="flex items-center gap-2">
            <FaUserTie className="w-4 h-4 text-gray-500" />
            <div className="text-sm font-medium text-gray-800 mr-2">
              Appoint To
            </div>
            <div className="flex-1">
              <StaffSelector
                appointment={staffSelectorAppointment}
                mode="update"
                employees={employees}
                index={0}
                handleAppointmentFieldChange={handleStaffSelectorChange}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-between px-6 py-4 border-t border-gray-200 rounded-b-2xl bg-gray-50">
          <button
            onClick={handleCancelAppointment}
            disabled={isCancelling}
            className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-200 ${
              isCancelling
                ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                : "bg-red-600 text-white hover:bg-red-700"
            }`}
          >
            <FaBan className="w-4 h-4" />
            {isCancelling ? "Rejecting..." : "Reject"}
          </button>

          <div className="flex gap-2">
            {appointment.status !== "completed" && appointment.status !== "closed" && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (onRescheduleClick) {
                    onRescheduleClick(appointment); // don't close tooltip here
                  }
                }}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all duration-200"
              >
                <FaEdit className="w-4 h-4" />
                Reschedule
              </button>
            )}
            <button
              onClick={handleConfirmAppointment}
              disabled={isSaving || !selectedStaff || !appointmentTime}
              className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-200 ${
                isSaving || !selectedStaff
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-green-600 text-white hover:bg-green-700"
              }`}
            >
              <FaCheck className="w-4 h-4" />
              {isSaving ? "Confirming..." : "Confirm"}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .animate-fade-in {
          animation: fadeIn 0.2s ease-out forwards;
        }
        @keyframes fadeIn {
          0% { opacity: 0; transform: translateY(-8px) scale(0.95); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </>
  );
};

export default AppointmentConfirmTooltip;